import { ActivatedRoute } from '@angular/router';
import {
  ChatMessage,
  CoachConversation,
  Inference,
} from '../models/coach.model';
import { CoachConversationMode } from '@app/coach-builder/models/coach-builder.model';

export const UTC_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const stopEvent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
};

export const processSavedConversation = (
  messages: ChatMessage[]
): ChatMessage[] =>
  messages?.map((msg: ChatMessage) => {
    const isUser = msg.senderType === 'User';
    return {
      messageText: msg.messageText,
      isUser: isUser,
      senderType: isUser ? 'User' : 'Coach',
      messageTimestamp: msg.messageTimestamp ?? new Date().toISOString(),
      ...msg,
    };
  });

export const toggleFooter = (show: boolean = false) => {
  const footer = document.querySelector('da-footer');
  const zendesk = document.querySelector('dgx-zendesk-widget');
  if (show) {
    footer?.classList.remove('tw-hidden');
    zendesk?.classList.remove('tw-hidden');
  } else {
    footer?.classList.add('tw-hidden');
    zendesk?.classList.add('tw-hidden');
  }
};

export const getHeaderAndFooterHeight = (
  includeHeader = true,
  includeFooter = true
) => {
  let totalHeight = 0;

  if (includeHeader) {
    const header = document.querySelector('da-header header');
    if (header) {
      totalHeight += header.getBoundingClientRect().height;
    }
  }

  if (includeFooter) {
    const footer = document.querySelector('da-footer');
    if (footer) {
      totalHeight += footer.getBoundingClientRect().height;
    }
  }

  return totalHeight;
};

export const getAverageAmplitude = (audioData: Uint8Array) => {
  if (audioData.length === 0) return 0;
  const sum = audioData.reduce((acc, val) => acc + val, 0);
  return sum / (audioData.length * 255); // Normalize to 0-1
};

export const getConversationId = (activatedRoute: ActivatedRoute) =>
  activatedRoute.snapshot?.params?.conversationId;

export const replaceUser = (text: string, user: string) =>
  text.replace(/<user>/g, user);

export const replaceValues = (
  text: string,
  replacements: Record<string, string>
) => {
  let result = text;
  for (const [key, value] of Object.entries(replacements)) {
    result = result.replace(new RegExp(`<${key}>`, 'g'), value);
  }
  return result;
};

export const extractSkillReport = (conversation: CoachConversation) => {
  const report = conversation?.inferences?.find(
    (inference: Inference) => inference.inferenceType === 'SkillReview'
  );
  if (!report) return;
  try {
    const skillReport = JSON.parse(report.inferredData);
    if (skillReport) {
      return skillReport;
    }
  } catch {
    return null;
  }
};

export const getConversationModes = (mode: string) => {
  let voiceMode = false;
  let textMode = false;
  let modes = ['text', 'voice'];
  switch (mode) {
    case CoachConversationMode.TextOnly:
      voiceMode = false;
      textMode = true;
      modes = [];
      break;
    case CoachConversationMode.VoiceOnly:
      voiceMode = true;
      textMode = false;
      modes = ['voice'];
      break;
    default:
      voiceMode = true;
      textMode = true;
      modes = ['text', 'voice'];
      break;
  }
  return { voiceMode, textMode, modes };
};
